/**
 * SHELL — durable submission queue.
 *
 * A run finished offline (or while Backend returns 501) must never be lost — that
 * is the whole loss-aversion premise. We persist the ReplayData and retry on
 * reconnect / next boot until the server accepts (or definitively rejects) it.
 *
 * Only TRANSPORT failures are retried. A 200 with status:'rejected' or a
 * 'duplicate' reason is terminal — we drop it (the server has spoken).
 */

import type { ReplayData } from '@/types';
import type { NetResult } from './client';
import type { ScoreSubmissionResult } from '@/types';

const KEY = 'dailyroll:submitQueue';
const MAX_QUEUE = 8;

interface QueuedSubmission {
  readonly dateKey: string;
  readonly replay: ReplayData;
  readonly queuedAt: string;
}

function load(): QueuedSubmission[] {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as QueuedSubmission[]) : [];
  } catch {
    return [];
  }
}

function save(items: QueuedSubmission[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(items.slice(-MAX_QUEUE)));
  } catch {
    /* ignore */
  }
}

export interface SubmitQueue {
  /** Persist a replay to retry later (deduped by dateKey). */
  enqueue(replay: ReplayData): void;
  /** Number of pending submissions. */
  size(): number;
  /** Attempt to flush all pending submissions. Returns accepted results. */
  flush(): Promise<ScoreSubmissionResult[]>;
  /** Start listening for reconnect events to auto-flush. Returns a disposer. */
  start(): () => void;
}

export function createSubmitQueue(
  submit: (replay: ReplayData) => Promise<NetResult<ScoreSubmissionResult>>,
  onAccepted?: (result: ScoreSubmissionResult) => void,
): SubmitQueue {
  let flushing = false;

  const queue: SubmitQueue = {
    enqueue(replay) {
      const items = load().filter((q) => q.dateKey !== replay.dateKey);
      items.push({ dateKey: replay.dateKey, replay, queuedAt: new Date().toISOString() });
      save(items);
    },

    size() {
      return load().length;
    },

    async flush() {
      if (flushing) return [];
      flushing = true;
      const accepted: ScoreSubmissionResult[] = [];
      try {
        const items = load();
        const remaining: QueuedSubmission[] = [];
        for (const item of items) {
          const res = await submit(item.replay);
          if (res.ok) {
            // Terminal either way: server accepted or definitively rejected.
            if (res.data.status === 'accepted') {
              accepted.push(res.data);
              onAccepted?.(res.data);
            }
            // accepted OR rejected => drop from queue (do not re-add).
          } else if (res.failure.kind === 'offline' || res.failure.kind === 'pending' || res.failure.kind === 'server' || res.failure.kind === 'rate_limited') {
            // Transient: keep for a later attempt.
            remaining.push(item);
          }
          // 'unauthorized' / 'error' => drop (a stale token or bad payload
          // won't fix itself by blind retry; a fresh run re-enqueues).
        }
        save(remaining);
      } finally {
        flushing = false;
      }
      return accepted;
    },

    start() {
      const onOnline = (): void => {
        void queue.flush();
      };
      window.addEventListener('online', onOnline);
      // Opportunistic flush shortly after boot.
      const t = window.setTimeout(onOnline, 1500);
      return () => {
        window.removeEventListener('online', onOnline);
        window.clearTimeout(t);
      };
    },
  };

  return queue;
}
